import { useMemo } from 'react';

import { ClockIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CampaignSubscriberWithJourney, ContactJourneyEntry } from '@/shared/types/campaigns';

interface ContactInfoCardProps {
  subscriber: CampaignSubscriberWithJourney;
}

// Timezone and location live in session context_vars.user, not subscriber-level context
export const getJourneySessionUser = (
  journey: ContactJourneyEntry[] | undefined
): Record<string, unknown> => {
  for (const entry of journey ?? []) {
    const user = entry.session?.context_vars?.user as Record<string, unknown> | undefined;
    if (user) {
      return user;
    }
  }
  return {};
};

export const ContactInfoCard = ({ subscriber }: ContactInfoCardProps) => {
  const { t } = useTranslation();

  const sessionUser = useMemo(
    () => getJourneySessionUser(subscriber.contact_journey),
    [subscriber.contact_journey]
  );

  const location = useMemo(() => {
    const loc = sessionUser.location as Record<string, unknown> | string | undefined;
    if (typeof loc === 'string') {
      return loc;
    }
    if (loc && typeof loc === 'object') {
      return (loc.country as string) || undefined;
    }
    // residence_state lives inside context (was hoisted out of top-level in 2026-04).
    return subscriber.context?.residence_state;
  }, [sessionUser.location, subscriber.context?.residence_state]);

  const fields = useMemo(
    () => [
      { key: 'phone', icon: PhoneIcon, value: subscriber.phone },
      { key: 'email', icon: MailIcon, value: subscriber.email },
      { key: 'timezone', icon: ClockIcon, value: sessionUser.timezone as string | undefined },
      { key: 'location', icon: MapPinIcon, value: location },
    ],
    [subscriber, sessionUser, location]
  );

  return (
    <Card className="rounded-md bg-background pt-0 text-foreground shadow-none">
      <CardHeader className="flex items-center border-b pt-3 pb-2.5! pl-5">
        <CardTitle className="text-base leading-none">
          {t('campaigns.subscriber_journey.contact_info')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {fields.map(({ key, icon: Icon, value }) => (
          <div key={key} className="flex items-center gap-3">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm">{value || 'N/A'}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
